// ─────────────────────────────────────────────────────────────────────────
// financeUtils — pure helpers for the Billing / Payroll finance module.
// No JSX, no antd: formatting, lifecycle gating, timesheet derivations, CSV.
//
// The backend exposes NO lifecycle guards on the finance action endpoints —
// any status can be set at any time. The gating functions here are therefore
// the ONLY guard: they fail closed (everything disabled) on unknown status.
// ─────────────────────────────────────────────────────────────────────────

import {
  currencySymbol,
  rateUnitShort,
  toNumber,
  formatMoney as formatMoneyShared,
} from '../../utils/currency.js';

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// ── Formatting ─────────────────────────────────────────────────────────────
// These delegate to the package-wide utils/currency helpers so the billing /
// payroll tables print a number exactly the way the finance approval panel and
// the detail view do. Both keep their old zero-argument behaviour ($ + hourly)
// as the DEFAULT, so every existing call site is unaffected, but the currency
// and the unit are now parameters — they used to be string literals, which is
// what made a non-USD, non-hourly engagement render wrongly.

export function formatMoney(value, currency = '$') {
  const n = toNumber(value);
  return formatMoneyShared(Number.isNaN(n) ? 0 : n, currencySymbol(currency), { decimals: 2 });
}

export function formatHours(value) {
  return `${num(value).toFixed(2)} hrs`;
}

// formatRate(62.5) → "$62.50/hr" | formatRate(500, '₹', 'day') → "₹500.00/day"
// An unresolvable unit yields the bare amount rather than a trailing slash.
export function formatRate(value, currency = '$', unit = 'hr') {
  const short = rateUnitShort(unit, '');
  const money = formatMoney(value, currency);
  return short ? `${money}/${short}` : money;
}

// formatPercent renders a rate like 9.25 → "9.25%", 5.8 → "5.8%", 5 → "5%"
// (up to two decimals, trailing zeros trimmed) for labelling a deduction with
// the percentage it was derived from.
export function formatPercent(value) {
  return `${parseFloat(num(value).toFixed(2))}%`;
}

export function initialsOf(name) {
  const words = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '·';
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

// ── Workflow events ────────────────────────────────────────────────────────

const WORKFLOW_EVENT_META = {
  TIMESHEET_APPROVED: { label: 'Timesheet Approved', icon: 'check', tone: 'success' },
  BILLING_READY: { label: 'Ready for Billing', icon: 'file', tone: 'info' },
  BILLING_INVOICED: { label: 'Invoice Generated', icon: 'file', tone: 'info' },
  BILLING_PAID: { label: 'Client Payment Received', icon: 'dollar', tone: 'success' },
  PAYROLL_PROCESSING: { label: 'Payroll Processing Started', icon: 'sync', tone: 'warning' },
  PAYROLL_PAID: { label: 'Payroll Disbursed', icon: 'dollar', tone: 'success' },
  PAYSLIP_GENERATED: { label: 'Payslip Generated', icon: 'file', tone: 'info' },
};

export function humanizeEvent(event) {
  const known = WORKFLOW_EVENT_META[event];
  if (known) return known;
  const label = String(event ?? '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
  return { label: label || 'Activity', icon: 'file', tone: 'neutral' };
}

export function getApprovalDate(workflow) {
  const hit = (Array.isArray(workflow) ? workflow : []).find((w) => w?.event === 'TIMESHEET_APPROVED');
  return hit?.at ?? null;
}

// ── Lifecycle gating (client-side only — fail closed) ──────────────────────

const enabled = (variant) => ({ enabled: true, variant, disabledReason: null });
const disabled = (reason) => ({ enabled: false, variant: 'default', disabledReason: reason });

export const BILLING_STATUS_FLOW = ['PaymentNotReceived', 'Invoiced', 'Paid'];
export const PAYROLL_STATUS_FLOW = ['Ready', 'Processing', 'Paid'];

export function getBillingActionStates(status) {
  switch (status) {
    case 'PaymentNotReceived':
      return {
        generateInvoice: enabled('primary'),
        recordPayment: disabled('Generate the invoice first'),
        markPaid: disabled('Generate the invoice first'),
      };
    case 'Invoiced':
      return {
        generateInvoice: disabled('Invoice already generated'),
        recordPayment: enabled('primary'),
        markPaid: enabled('default'),
      };
    // Derived from the client-payment receipts log once money has moved
    // (financeActualsSync.go deriveBillingStatus). Partially Received sits
    // between Invoiced and fully paid: the invoice exists and still has a
    // balance, so recording the next installment must stay available.
    case 'Partially Received':
      return {
        generateInvoice: disabled('Invoice already generated'),
        recordPayment: enabled('primary'),
        markPaid: enabled('default'),
      };
    // Received is the fully-paid terminal state — reached automatically the
    // moment the receipts cover the invoice — so it gates like Paid.
    case 'Received':
    case 'Paid':
      return {
        generateInvoice: disabled('Payment already received'),
        recordPayment: disabled('Payment already received'),
        markPaid: disabled('Payment already received'),
      };
    default:
      return {
        generateInvoice: disabled('Unavailable in the current status'),
        recordPayment: disabled('Unavailable in the current status'),
        markPaid: disabled('Unavailable in the current status'),
      };
  }
}

// Payslips are generated (and sent) AFTER the money has actually moved, never
// before — so Generate Payslip only unlocks once the payroll is Paid, following
// Process -> Mark Paid -> Generate Payslip in that order.
export function getPayrollActionStates(status) {
  switch (status) {
    case 'Ready':
      return {
        process: enabled('primary'),
        generatePayslip: disabled('Mark the payroll paid first'),
        markPaid: disabled('Process the payroll first'),
      };
    case 'Processing':
      return {
        process: disabled('Payroll is already processing'),
        generatePayslip: disabled('Mark the payroll paid first'),
        markPaid: enabled('primary'),
      };
    case 'Paid':
      return {
        process: disabled('Payroll already paid'),
        generatePayslip: enabled('primary'),
        markPaid: disabled('Payroll already paid'),
      };
    default:
      return {
        process: disabled('Unavailable in the current status'),
        generatePayslip: disabled('Unavailable in the current status'),
        markPaid: disabled('Unavailable in the current status'),
      };
  }
}

// Three-step progress strip. Step 1 is always done: a finance record only
// exists after the timesheet is approved.
export function getWorkflowSteps(type, status) {
  if (type === 'billing') {
    const reached = Math.max(BILLING_STATUS_FLOW.indexOf(status), 0);
    return [
      { key: 'approved', label: 'Timesheet Approved', state: 'done' },
      { key: 'invoiced', label: 'Invoice Raised', state: reached >= 1 ? 'done' : 'current' },
      { key: 'paid', label: 'Payment Received', state: reached >= 2 ? 'done' : reached === 1 ? 'current' : 'pending' },
    ];
  }
  const reached = Math.max(PAYROLL_STATUS_FLOW.indexOf(status), 0);
  return [
    { key: 'approved', label: 'Timesheet Approved', state: 'done' },
    { key: 'processing', label: 'Processing', state: reached >= 1 ? 'done' : 'current' },
    { key: 'paid', label: 'Paid', state: reached >= 2 ? 'done' : reached === 1 ? 'current' : 'pending' },
  ];
}

// ── Timesheet derivations ──────────────────────────────────────────────────

const dayLeaveHours = (d) => num(d?.leave?.CL) + num(d?.leave?.SL);
const daySt = (d) => num(d?.workingHours?.ST);
const dayOt = (d) => num(d?.workingHours?.OT);

export function deriveDayStats(dailyLogs) {
  const logs = Array.isArray(dailyLogs) ? dailyLogs : [];
  const stats = {
    loggedDays: 0,
    approvedDays: 0,
    rejectedDays: 0,
    pendingDays: 0,
    leaveDays: 0,
    holidayDays: 0,
    weekOffDays: 0,
    stHours: 0,
    otHours: 0,
    leaveHours: 0,
    totalHours: 0,
    avgHoursPerLoggedDay: 0,
  };
  logs.forEach((d) => {
    const st = daySt(d);
    const ot = dayOt(d);
    const leave = dayLeaveHours(d);
    const logged = Boolean(d?.isLogged) || st > 0 || ot > 0;
    if (logged) {
      stats.loggedDays += 1;
      if (d?.status === 'approved') stats.approvedDays += 1;
      else if (d?.status === 'rejected') stats.rejectedDays += 1;
      else stats.pendingDays += 1;
    }
    if (leave > 0) stats.leaveDays += 1;
    if (d?.isHoliday) stats.holidayDays += 1;
    if (d?.isWeekOff) stats.weekOffDays += 1;
    stats.stHours += st;
    stats.otHours += ot;
    stats.leaveHours += leave;
    stats.totalHours += st + ot;
  });
  stats.avgHoursPerLoggedDay = stats.loggedDays ? stats.totalHours / stats.loggedDays : 0;
  return stats;
}

// Rows for the daily breakdown table: logged days plus leave/holiday markers.
export function buildDailyRows(dailyLogs, rate) {
  const logs = Array.isArray(dailyLogs) ? dailyLogs : [];
  return logs
    .filter((d) => Boolean(d?.isLogged) || daySt(d) > 0 || dayOt(d) > 0 || dayLeaveHours(d) > 0 || d?.isHoliday)
    .map((d) => {
      const st = daySt(d);
      const ot = dayOt(d);
      const total = st + ot;
      return {
        key: d.date,
        date: d.date,
        day: d.day || '',
        st,
        ot,
        leave: dayLeaveHours(d),
        isHoliday: Boolean(d?.isHoliday),
        total,
        amount: total * num(rate),
        status: d?.status || 'logged',
        reviewer: d?.reviewedByName || '',
        remarks: d?.rejectionReason || d?.notes || '',
      };
    });
}

// Parse "YYYY-MM-DD" as a LOCAL date (new Date('YYYY-MM-DD') would parse UTC
// and can shift the weekday across timezones).
function parseLocalDate(dateStr) {
  const [y, m, d] = String(dateStr ?? '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const shortDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Monday-based weekly rollup (matches the backend's Monday week boundaries).
export function buildWeeklyRollup(dailyLogs, rate) {
  const logs = Array.isArray(dailyLogs) ? dailyLogs : [];
  const weeks = new Map();
  logs.forEach((d) => {
    const date = parseLocalDate(d?.date);
    if (!date) return;
    const st = daySt(d);
    const ot = dayOt(d);
    const leave = dayLeaveHours(d);
    if (!d?.isLogged && st <= 0 && ot <= 0 && leave <= 0) return;
    const mondayOffset = (date.getDay() + 6) % 7;
    const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate() - mondayOffset);
    const key = weekStart.getTime();
    if (!weeks.has(key)) {
      const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
      weeks.set(key, {
        weekStart,
        weekEnd,
        label: `${shortDate(weekStart)} – ${shortDate(weekEnd)}`,
        st: 0,
        ot: 0,
        leaveHours: 0,
        total: 0,
        amount: 0,
        days: 0,
      });
    }
    const week = weeks.get(key);
    week.st += st;
    week.ot += ot;
    week.leaveHours += leave;
    week.total += st + ot;
    week.amount += (st + ot) * num(rate);
    if (Boolean(d?.isLogged) || st > 0 || ot > 0) week.days += 1;
  });
  return [...weeks.values()].sort((a, b) => a.weekStart - b.weekStart);
}

export function computeMarginBand(marginPercent, { high = 25, low = 10 } = {}) {
  const pct = num(marginPercent);
  if (pct >= high) return 'high';
  if (pct < low) return 'low';
  return 'mid';
}

// ── CSV export (client-side, RFC-4180 escaping) ────────────────────────────

function csvCell(value) {
  const text = value == null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildCsv(rows, columns) {
  const header = columns.map((c) => csvCell(c.label)).join(',');
  const lines = (rows ?? []).map((row) =>
    columns.map((c) => csvCell(c.format ? c.format(row[c.key], row) : row[c.key])).join(','),
  );
  return [header, ...lines].join('\r\n');
}

// ── Admin field config (labels + status colors from userFieldConfig) ───────
// `fields` is the `fields` array the /module-data-list response attaches for
// module=billing|payroll — each entry is a FieldVisibilityConfig: { field,
// label, renderType, valueStyles:[{value,label,color,bg,variant}], ... }.
// These helpers let the finance table keep its own hand-built columns/cells
// but source the LABEL and STATUS COLOR from admin config when present,
// falling back to the built-in defaults when no config field matches.

export function indexFieldConfig(fields) {
  const byKey = new Map();
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    const key = field?.field ?? field?.key ?? field?.value;
    if (key) byKey.set(key, field);
  });
  return byKey;
}

export function getFieldLabel(fields, key, fallback) {
  const byKey = fields instanceof Map ? fields : indexFieldConfig(fields);
  return byKey.get(key)?.label || fallback;
}

// Matches case-insensitively on value or label, mirroring the ListView engine
// (CORE-THEME/src/components/ListView.jsx getBadgeValueStyle).
export function getValueStyle(fields, key, value) {
  const byKey = fields instanceof Map ? fields : indexFieldConfig(fields);
  const styles = byKey.get(key)?.valueStyles;
  if (!Array.isArray(styles) || !styles.length || value == null) return null;
  const needle = String(value).toLowerCase();
  const match = styles.find(
    (s) => String(s.value ?? '').toLowerCase() === needle || String(s.label ?? '').toLowerCase() === needle,
  );
  if (!match) return null;
  return { color: match.color, bg: match.bg ?? match.background ?? match.backgroundColor, label: match.label };
}

export function downloadCsv(filename, csv) {
  // Leading BOM so Excel opens the file as UTF-8.
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
